// ═══════════════════════════════════════════════════════════
// Вкладка «Задачі» в картці клієнта.
//
// Показує відкриті задачі розгорнуто, а закриті ховає за одним рядком
// «Історія: N задач». У клієнта, з яким працюють роками, закритих задач
// накопичуються десятки, і показувати їх нарівні з поточними означає
// сховати три важливі рядки серед сорока неважливих.
//
// Дані тягнуться лише коли вкладку відкрили: більшість відкриттів картки —
// це «глянути телефон», і платити за них зайвим запитом нема за що.
// ═══════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAppData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import { apiFetch } from '../../services/api';
import {
  TaskPanel, TaskFormModal, useTaskUsers,
  STATUS_LABEL, TYPE_LABEL, OUTCOME_LABEL, fmtDue, fmtWhen, isOpenStatus,
} from './TaskShared';

// «Звʼязатися пізніше» (ТЗ телефонії, 21.10): задача «контакт з клієнтом»
// на завтра 10:00 з привʼязкою до клієнта — дата й виконавець правляться у
// формі. contactLaterSignal змінюється — форма відкривається одразу (кнопка
// в шапці картки).
function tomorrowDate() {
  const d = new Date(Date.now() + 24 * 3600_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function CustomerTasksTab({ customer, activeLocationId, contactLaterSignal = 0 }) {
  const { currentUser, userProfile, hasPermission } = useAuth();
  const { subscribeSync } = useAppData();
  const toast = useToast();

  const canCreate = hasPermission('tasks_create');
  const canAssign = hasPermission('tasks_assign');
  const users = useTaskUsers(canCreate);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createDefaults, setCreateDefaults] = useState(null);
  const openContactLater = useCallback(() => {
    setCreateDefaults({
      title: `Звʼязатися: ${customer?.name || 'клієнт'}`,
      type: 'client_contact',
      dueDate: tomorrowDate(),
      dueTime: '10:00',
    });
    setShowCreate(true);
  }, [customer?.name]);
  useEffect(() => {
    if (contactLaterSignal && canCreate && customer?.id) openContactLater();
  }, [contactLaterSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  const reload = useCallback(() => setReloadTick(n => n + 1), []);
  useEffect(() => (subscribeSync ? subscribeSync('tasks', reload) : undefined), [subscribeSync, reload]);

  useEffect(() => {
    if (!customer?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ customer_id: String(customer.id), page_size: '100' });
    apiFetch(`/api/analytics/tasks-index?${params}`)
      .then(r => { if (!cancelled) setItems(r.items || []); })
      .catch(err => { if (!cancelled) toast.error(err?.message || 'Не вдалося завантажити задачі'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customer?.id, reloadTick, toast]);

  const patchTask = useCallback((task) => {
    setItems(prev => prev.map(t => (t.id === task.id ? task : t)));
    setSelected(prev => (prev && prev.id === task.id ? task : prev));
  }, []);

  const { open, closed } = useMemo(() => ({
    open: items.filter(t => isOpenStatus(t.status)),
    closed: items.filter(t => !isOpenStatus(t.status)),
  }), [items]);

  const lockedCustomer = customer
    ? { id: customer.id, name: customer.name, phone: customer.phone }
    : null;

  return (
    <div className="cust-tasks">
      <div className="cust-tasks-head">
        <div className="tasks-section-title">
          Відкриті задачі{open.length ? <span className="tasks-group-count">{open.length}</span> : null}
        </div>
        {canCreate && customer?.id && (
          <span className="cust-tasks-actions">
            <button type="button" className="btn btn-secondary btn-sm" onClick={openContactLater}>
              📞 Звʼязатися пізніше
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setCreateDefaults(null); setShowCreate(true); }}>
              + Задача
            </button>
          </span>
        )}
      </div>

      {loading ? (
        <div className="tasks-loading">Завантаження…</div>
      ) : (
        <>
          {!open.length ? (
            <div className="tasks-empty-note">Відкритих задач по клієнту немає</div>
          ) : open.map(t => (
            <button
              key={t.id} type="button"
              className={`myday-item${t.isOverdue ? ' overdue' : ''}`}
              onClick={() => setSelected(t)}
            >
              <span className={`tasks-prio tasks-prio--${t.priority}`} aria-hidden="true" />
              <span className="myday-item-body">
                <span className="myday-item-title">{t.title}</span>
                <span className="myday-item-sub">
                  {STATUS_LABEL[t.status]} · {TYPE_LABEL[t.type]}
                  {t.assigneeName ? ` · ${t.assigneeName}` : ''}
                  {t.dueAt ? ` · ${fmtDue(t.dueAt)}` : ''}
                </span>
              </span>
            </button>
          ))}

          {!!closed.length && (
            <div className="cust-tasks-history">
              <button
                type="button" className="cust-tasks-history-toggle"
                onClick={() => setShowHistory(v => !v)}
              >
                <span>{showHistory ? '▾' : '▸'} Історія: {closed.length} задач</span>
              </button>
              {showHistory && closed.map(t => (
                <button
                  key={t.id} type="button" className="myday-item"
                  onClick={() => setSelected(t)}
                >
                  <span className={`tasks-status tasks-status--${t.status}`}>{STATUS_LABEL[t.status]}</span>
                  <span className="myday-item-body">
                    <span className="myday-item-title">{t.title}</span>
                    <span className="myday-item-sub">
                      {fmtWhen(t.completedAt || t.cancelledAt || t.updatedAt)}
                      {t.completedBy?.name ? ` · ${t.completedBy.name}` : ''}
                      {t.outcome ? ` · ${OUTCOME_LABEL[t.outcome]}` : ''}
                      {t.outcomeAmount ? ` · ${t.outcomeAmount} ₴` : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {selected && (
        <TaskPanel
          task={selected}
          onClose={() => setSelected(null)}
          onPatch={patchTask}
          onReload={reload}
          onEdit={() => setEditing(selected)}
        />
      )}

      {(showCreate || editing) && (
        <TaskFormModal
          task={editing}
          users={users}
          canAssign={canAssign}
          currentUser={currentUser}
          userProfile={userProfile}
          activeLocationId={activeLocationId}
          lockedCustomer={editing ? null : lockedCustomer}
          defaults={editing ? null : createDefaults}
          onClose={() => { setShowCreate(false); setEditing(null); setCreateDefaults(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); setCreateDefaults(null); reload(); }}
        />
      )}
    </div>
  );
}
